import { Deal } from '../data/mock';
import { supabase } from '../services/supabaseClient';
import { logger } from '../utils/logger';
import { withTimeout } from '../utils/helpers';

/**
 * v13.22 — ترطيب التقييمات لمجموعة عروض (صفحة واحدة).
 * كان هذا المنطق محشوراً داخل getAll ويعمل على **كل** العروض؛ استُخرج ليعمل
 * على الصفحة المطلوبة فقط، فيبقى الطلب صغيراً مهما كبر الكتالوج.
 */
const hydrateRatings = async (deals: Deal[]): Promise<Deal[]> => {
    if (deals.length === 0) return deals;
    const ids = deals.map(d => d.id);
    const { data: ratingRows } = await supabase
        .from('ratings')
        .select('*')
        .in('deal_id', ids)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        // v13.29 — سقف. منتج رائج بعشرات آلاف التقييمات كان سيُنزّلها كلها مع
        // الصفحة. المتوسط والعدد يأتيان من `rating_avg`/`rating_count` المثبّتين
        // على الصف، وهذه القائمة للعرض فقط — فالسقف لا يُفقد رقماً.
        .limit(RATINGS_HYDRATE_CAP);
    const byDeal: Record<string, any[]> = {};
    for (const r of (ratingRows || [])) {
        (byDeal[r.deal_id] ||= []).push({
            id: r.id,
            userId: r.user_id,
            userName: r.user_name,
            score: Number(r.score) || 0,
            comment: r.comment ?? '',
            // بتوقيت الجهاز — toISOString (UTC) يرجع اليوم للوراء قبل ٣ فجراً
            date: r.created_at ? (() => { const d = new Date(r.created_at); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })() : '',
            reply: r.reply ?? undefined,
            repliedBy: r.replied_by ?? undefined,
            repliedAt: r.replied_at ?? undefined,
            likedBy: Array.isArray(r.liked_by) ? r.liked_by : [],
            likeCount: Number(r.like_count) || 0,
        });
    }
    for (const d of deals) d.ratings = byDeal[d.id] || [];
    return deals;
};

/** مؤشّر الصفحة التالية (keyset) — آخر عنصر رأيناه. */
export interface DealCursor { createdAt: number; id: string; }

/** حجم صفحة الواجهة الافتراضي — يملأ عدة شاشات دون إثقال أول رسمة. */
export const DEALS_PAGE_SIZE = 30;

/** سقف صفوف التقييمات المجلوبة مع صفحة عروض (العرض فقط — الإحصاء مثبَّت). */
const RATINGS_HYDRATE_CAP = 300;

// ─────────────────────────────────────────────────────────────────────────────
// v13.24 — محرك التصفّح التجاري (browse)
//
// المشكلة التي يحلّها: بعد ترقيم v13.22 صارت `deals` **نافذة** من ٣٠ عرضاً،
// بينما الترشيح والترتيب بقيا في المتصفح على تلك النافذة. عملياً:
//   • «الأقل سعراً» كانت ترتّب أرخص ٣٠ محمَّلاً، لا أرخص الكتالوج.
//   • فلترة مدينة صغيرة تعطي «لا نتائج» رغم وجود آلاف العروض فيها.
//   • العدّاد أعلى الصفحة يعرض حجم النافذة لا عدد المنتجات الحقيقي.
// الحل: نداء واحد `browse_deals` يفعل كل شيء على القاعدة (ترشيح + ترتيب +
// بحث + عدّ + مؤشّر keyset) — فما تراه الواجهة هو ما في القاعدة حرفياً.
// ─────────────────────────────────────────────────────────────────────────────

export type BrowseSort = 'new' | 'price' | 'discount' | 'reliability' | 'best' | 'soon';

/** مؤشّر keyset مُعمَّم: قيمة مفتاح الترتيب + مُعرّف الفاصل الحاسم. */
export interface BrowseCursor { key: number; id: string; }

export interface BrowseParams {
    query?: string | null;
    category?: string | null;
    gender?: string | null;
    region?: string | null;
    city?: string | null;
    mall?: string | null;
    storeId?: string | null;
    seasonId?: string | null;
    sort?: BrowseSort;
    mode?: 'live' | 'coming_soon';
    openNow?: boolean;
    verified?: boolean;
    /** التجار الذين حظرهم المشتري — يُستبعدون على القاعدة لا في المتصفح. */
    blocked?: string[] | null;
    /** استبعاد عروض ظهرت في نداء سابق (يُستخدم للتوسّع التدريجي في الأشرطة). */
    excludeIds?: string[] | null;
    cursor?: BrowseCursor | null;
    limit?: number;
}

export interface BrowseResult {
    deals: Deal[];
    /** عدد المطابقات الكلي في القاعدة — لا عدد المُحمّل. مسقوف بـ5000. */
    total: number;
    /** true = المطابقات تتجاوز السقف، فيُعرض «+5000». */
    totalCapped: boolean;
    hasMore: boolean;
    cursor: BrowseCursor | null;
}

const EMPTY_BROWSE: BrowseResult = { deals: [], total: 0, totalCapped: false, hasMore: false, cursor: null };

/**
 * أعمدة العرض التي تحتاجها الواجهة.
 *
 * v13.24 — استبدلت `select('*')`. القاعدة صار فيها ثلاثة أعمدة بحث **مُولَّدة**
 * (`search_vec` و`name_norm` و`loc_keys`) تخدم الفهارس ولا تعني الواجهة في شيء؛
 * لو بقي `*` لانتقل متجه البحث الكامل لكل عرض عبر شبكة الجوال بلا فائدة.
 * `browse_deals` تُسقطها من جهة الخادم أصلاً، وهذه القائمة تفعل الشيء نفسه
 * للمسارات المباشرة.
 *
 * ⚠️ عند إضافة عمود جديد لجدول `deals`: أضفه هنا وفي `mapRowToDeal` معاً.
 */
export const DEAL_SELECT = [
    'id', 'store_id', 'shop_name', 'item_name', 'category', 'gender', 'size',
    'original_price', 'discounted_price', 'discount_percentage', 'images', 'description',
    'location_id', 'custom_location_name', 'google_maps_link', 'map_lat', 'map_lng',
    'reliability_score', 'expires_in_minutes', 'quantity', 'is_unlimited', 'initial_quantity',
    'on_hand', 'sold_out',
    'prep_time', 'status', 'created_at', 'expiry_hijri', 'expiry_gregorian', 'updated_at',
    'city', 'region', 'expiry_type', 'expiry_date', 'views', 'clicks', 'starts_at',
    'coming_soon_notified_at', 'subscription_frozen', 'source', 'max_per_booking',
    'max_bookings_per_buyer', 'rebook_cooldown_minutes', 'season_id', 'options', 'variants',
    'pos_sku', 'locations', 'loc_qty_mode', 'auth_real_count', 'auth_fake_count',
    'rating_avg', 'rating_count',
].join(',');

/** عرضٌ مرتبط بفرع محدد + مسافته عن المستخدم (صفحة «حولي»). */
export interface NearbyDeal extends Deal {
    distance: number;
    lat: number;
    lng: number;
    /** مفتاح فريد للفرع: `<dealId>__<branchId>` — العرض الواحد قد يظهر لفروعه. */
    _key: string;
    _bId?: string;
    _bLocId?: string | null;
    _bName?: string;
    _bRegion?: string;
    _bCity?: string;
}

export interface NearbyParams {
    lat: number;
    lng: number;
    /** 0 = بلا حد مسافة. */
    radiusKm?: number;
    query?: string | null;
    category?: string | null;
    region?: string | null;
    city?: string | null;
    locationId?: string | null;
    locType?: string | null;
    openNow?: boolean;
    blocked?: string[] | null;
    cursor?: { dist: number; key: string } | null;
    limit?: number;
}

export interface NearbyResult {
    deals: NearbyDeal[];
    total: number;
    totalCapped: boolean;
    hasMore: boolean;
    cursor: { dist: number; key: string } | null;
}

export const dealRepository = {
    /**
     * v13.26 — «حولي» على مستوى **الفرع** ومن الخادم.
     *
     * كانت الصفحة توسّع فروع العروض وتحسب المسافات في المتصفح على النافذة
     * المُحمّلة — أي أن «أقرب العروض إليك» كانت أقرب ما بين آخر ٣٠ عرضاً.
     * الآن القاعدة تقصّ الجوار بمرشّح صندوقي على فهرس الإحداثيات ثم تحسب
     * المسافة الدقيقة للناجين فقط، وترتّب بالأقرب مع ترقيم keyset.
     */
    browseNearby: async (p: NearbyParams): Promise<NearbyResult> => {
        const empty: NearbyResult = { deals: [], total: 0, totalCapped: false, hasMore: false, cursor: null };
        if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return empty;
        try {
            const { data, error } = await withTimeout(
                supabase.rpc('browse_nearby', {
                    p_lat: p.lat,
                    p_lng: p.lng,
                    p_radius_km: p.radiusKm ?? 0,
                    p_query: p.query?.trim() || null,
                    p_category: p.category && p.category !== 'all' ? p.category : null,
                    p_region: p.region || null,
                    p_city: p.city || null,
                    p_location_id: p.locationId || null,
                    p_loc_type: p.locType || null,
                    p_open_now: !!p.openNow,
                    p_blocked: (p.blocked && p.blocked.length) ? p.blocked : null,
                    p_limit: Math.max(1, Math.min(60, p.limit ?? DEALS_PAGE_SIZE)),
                    p_cursor_dist: p.cursor ? p.cursor.dist : null,
                    p_cursor_key: p.cursor ? p.cursor.key : null,
                }) as unknown as Promise<{ data: any; error: any }>,
                20000
            );
            if (error) throw error;
            const rows: any[] = Array.isArray(data?.rows) ? data.rows : [];
            const deals = rows.map(r => Object.assign(dealRepository.mapRowToDeal(r), {
                distance: Number(r.distance) || 0,
                lat: Number(r.lat) || 0,
                lng: Number(r.lng) || 0,
                _key: String(r._key),
                _bId: r._bId ?? undefined,
                _bLocId: r._bLocId ?? undefined,
                _bName: r._bName ?? undefined,
                _bRegion: r._bRegion ?? undefined,
                _bCity: r._bCity ?? undefined,
            }) as NearbyDeal);
            return {
                deals,
                total: Number(data?.total) || 0,
                totalCapped: !!data?.total_capped,
                hasMore: !!data?.has_more,
                cursor: (data?.has_more && data?.next_key != null && data?.next_dist != null)
                    ? { dist: Number(data.next_dist), key: String(data.next_key) }
                    : null,
            };
        } catch (e) {
            console.error('❌ browse_nearby failed:', e);
            return empty;
        }
    },

    /**
     * v13.24 — صفحة تصفّح/بحث كاملة من الخادم: نداء واحد يعيد العروض + العدد
     * الكلي + مؤشّر الصفحة التالية.
     *
     * لماذا نداء واحد: كل جولة شبكة إضافية تُضاف مباشرة لزمن ظهور الصفحة على
     * جوال المستخدم. العدّادات (التقييم/الأصالة) صارت مثبّتة على صف العرض
     * بـtriggers، فلا حاجة لجلب صفوف التقييمات ولا لنداء عدّ الأصالة.
     */
    browse: async (p: BrowseParams = {}): Promise<BrowseResult> => {
        try {
            const { data, error } = await withTimeout(
                supabase.rpc('browse_deals', {
                    p_query:      p.query?.trim() || null,
                    p_category:   p.category && p.category !== 'all' ? p.category : null,
                    p_gender:     p.gender && p.gender !== 'all' ? p.gender : null,
                    p_region:     p.region || null,
                    p_city:       p.city || null,
                    p_mall:       p.mall || null,
                    p_store_id:   p.storeId || null,
                    p_season_id:  p.seasonId || null,
                    p_sort:       p.sort || 'new',
                    p_mode:       p.mode || 'live',
                    p_open_now:   !!p.openNow,
                    p_verified:   !!p.verified,
                    p_cursor_key: p.cursor ? p.cursor.key : null,
                    p_cursor_id:  p.cursor ? p.cursor.id : null,
                    p_limit:      Math.max(1, Math.min(60, p.limit ?? DEALS_PAGE_SIZE)),
                    p_blocked:      (p.blocked && p.blocked.length) ? p.blocked : null,
                    p_exclude_ids:  (p.excludeIds && p.excludeIds.length) ? p.excludeIds : null,
                }) as unknown as Promise<{ data: any; error: any }>,
                20000
            );
            if (error) throw error;
            const rows: any[] = Array.isArray(data?.rows) ? data.rows : [];
            const nextKey = data?.next_key;
            const nextId  = data?.next_id;
            return {
                deals: rows.map(dealRepository.mapRowToDeal),
                total: Number(data?.total) || 0,
                totalCapped: !!data?.total_capped,
                hasMore: !!data?.has_more,
                cursor: (data?.has_more && nextId != null && nextKey != null)
                    ? { key: Number(nextKey), id: String(nextId) }
                    : null,
            };
        } catch (e) {
            console.error('❌ browse_deals failed:', e);
            return EMPTY_BROWSE;
        }
    },

    /**
     * v13.22 — صفحة من واجهة العروض بترقيم **keyset** (أسلوب التطبيقات الكبرى).
     *
     * لماذا keyset لا OFFSET: مع OFFSET تُجبَر القاعدة على عدّ وتخطّي كل الصفوف
     * السابقة، فتتباطأ الصفحة كلما تعمّق المستخدم (الصفحة ١٠٠ أبطأ ١٠٠ مرة من
     * الأولى). أما keyset فيسأل «أعطني ما بعد هذا العنصر» فيقفز الفهرس مباشرة —
     * زمن ثابت مهما بلغ عمق التمرير أو حجم الكتالوج.
     *
     * الترتيب `created_at DESC, id DESC` يطابق الفهرس `idx_deals_feed_keyset`
     * تماماً، و`id` فاصلٌ حاسم يمنع تكرار أو تخطّي عرض عند تساوي الطابع الزمني.
     */
    getFeedPage: async (opts?: { limit?: number; cursor?: DealCursor | null }): Promise<{ deals: Deal[]; nextCursor: DealCursor | null; hasMore: boolean }> => {
        const limit = Math.max(1, opts?.limit ?? DEALS_PAGE_SIZE);
        try {
            let q = supabase.from('deals').select(DEAL_SELECT).neq('status', 'deleted');
            const c = opts?.cursor;
            if (c) {
                // (created_at < c) OR (created_at = c AND id < c.id)
                q = q.or(`created_at.lt.${c.createdAt},and(created_at.eq.${c.createdAt},id.lt.${JSON.stringify(c.id)})`);
            }
            const { data, error } = await q
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .limit(limit + 1);           // +1 لمعرفة وجود المزيد بلا عدّ إضافي
            if (error) throw error;
            const rows = data || [];
            const hasMore = rows.length > limit;
            const page = rows.slice(0, limit).map(dealRepository.mapRowToDeal);
            await hydrateRatings(page);
            const last = page[page.length - 1];
            return {
                deals: page,
                nextCursor: (hasMore && last) ? { createdAt: Number(last.createdAt) || 0, id: last.id } : null,
                hasMore,
            };
        } catch (e) {
            console.error('❌ Deal feed page fetch failed:', e);
            return { deals: [], nextCursor: null, hasMore: false };
        }
    },

    /**
     * بحث سريع يعيد أفضل المطابقات فقط (شريط بحث الرئيسية).
     *
     * v13.24 — صار غلافاً رفيعاً حول `browse` بدل استعلام `ilike` مستقل:
     * مسار بحث **واحد** في كل المنصة، فلا يمكن أن تختلف نتيجة الرئيسية عن
     * نتيجة صفحة العروض. الترتيب `best` = الأفضل مطابقةً لا الأحدث.
     */
    searchDeals: async (opts: { query?: string; category?: string | null; limit?: number }): Promise<Deal[]> => {
        const q = (opts.query || '').trim();
        const cat = opts.category && opts.category !== 'all' ? opts.category : null;
        if (!q && !cat) return [];
        const { deals } = await dealRepository.browse({
            query: q || null,
            category: cat,
            sort: q ? 'best' : 'new',
            limit: opts.limit ?? 60,
        });
        return deals;
    },

    /**
     * v13.22 — كل عروض متجر واحد (لوحة التاجر + صفحة المتجر).
     * كانت هاتان الصفحتان تُرشّحان المصفوفة العامة، فمع الترقيم كانتا سترى ما
     * حُمّل في النافذة فقط. الاستعلام الموجّه يضمن اكتمالها دائماً — وهو أسرع
     * أصلاً (فهرس `idx_deals_store_created`).
     */
    getByStore: async (storeId: string): Promise<Deal[]> => {
        try {
            const { data, error } = await supabase
                .from('deals').select(DEAL_SELECT)
                .eq('store_id', storeId)
                .neq('status', 'deleted')
                .order('created_at', { ascending: false });
            if (error) throw error;
            const list = (data || []).map(dealRepository.mapRowToDeal);
            await hydrateRatings(list);
            return list;
        } catch (e) {
            console.error('❌ Store deals fetch failed:', e);
            return [];
        }
    },

    getAll: async (): Promise<Deal[]> => {
        try {
            const { data, error } = await supabase.from('deals').select(DEAL_SELECT).neq('status', 'deleted').order('created_at', { ascending: false });
            if (error) throw error;
            if (data) {
                const mappedData: Deal[] = data.map(dealRepository.mapRowToDeal);
                // Hydrate ratings from the dedicated table in one round trip.
                // Empty deal list → skip the second query entirely.
                if (mappedData.length > 0) {
                    const ids = mappedData.map(d => d.id);
                    const { data: ratingRows } = await supabase
                        .from('ratings')
                        .select('*')
                        .in('deal_id', ids)
                        .is('deleted_at', null)
                        .order('created_at', { ascending: false });
                    if (Array.isArray(ratingRows) && ratingRows.length > 0) {
                        const byDeal: Record<string, any[]> = {};
                        for (const r of ratingRows) {
                            (byDeal[r.deal_id] ||= []).push({
                                id: r.id,
                                userId: r.user_id,
                                userName: r.user_name,
                                score: Number(r.score) || 0,
                                comment: r.comment ?? '',
                                // بتوقيت الجهاز — toISOString (UTC) يرجع اليوم للوراء قبل ٣ فجراً بتوقيت السعودية
                                date: r.created_at ? (() => { const d = new Date(r.created_at); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })() : '',
                                reply: r.reply ?? undefined,
                                repliedBy: r.replied_by ?? undefined,
                                repliedAt: r.replied_at ?? undefined,
                                likedBy: Array.isArray(r.liked_by) ? r.liked_by : [],
                                likeCount: Number(r.like_count) || 0,
                            });
                        }
                        for (const d of mappedData) {
                            d.ratings = byDeal[d.id] || [];
                        }
                    }
                    // Hydrate real/fake authenticity vote counts (+ caller's own
                    // vote) in one round trip via the DEFINER RPC. Best-effort:
                    // a failure just leaves the badge hidden. v11.97
                    try {
                        const { authenticityRepository } = await import('./authenticityRepository');
                        const counts = await authenticityRepository.counts(ids);
                        for (const d of mappedData) {
                            const c = counts[d.id];
                            if (c) { d.authReal = c.realCount; d.authFake = c.fakeCount; d.myAuthVote = c.myVote; }
                        }
                    } catch (e) {
                        logger.log('authenticity counts skipped:', (e as any)?.message);
                    }
                }
                logger.log('📡 Fetched deals from remote:', mappedData.length);
                return mappedData;
            }
            return [];
        } catch (e) {
            console.error('❌ Remote deals fetch failed:', e);
            return [];
        }
    },

    getById: async (id: string): Promise<Deal | undefined> => {
        try {
            const { data, error } = await supabase.from('deals').select(DEAL_SELECT).eq('id', id).maybeSingle();
            if (error) throw error;
            return data ? dealRepository.mapRowToDeal(data) : undefined;
        } catch (e) {
            console.error('❌ Remote deal fetch by id failed:', e);
            return undefined;
        }
    },

    /**
     * v14.63 — جلب عروضٍ بمعرّفاتها. صفحةُ «المفضلة» تحتاجها لأن `deals` في
     * السياق **نافذةٌ مرقّمة** (keyset): مفضلةٌ قديمة خارج النافذة لا تظهر
     * أبداً لو اكتفينا بترشيح المصفوفة — وهو نفس الفخّ الموثّق في
     * صفحات المتجر والروابط المباشرة. القطع على ١٠٠ لأن `.in()` تذهب في سطر
     * العنوان وله حدّ طول.
     */
    getByIds: async (ids: string[]): Promise<Deal[]> => {
        if (!ids.length) return [];
        const CHUNK = 100;
        const out: Deal[] = [];
        try {
            for (let i = 0; i < ids.length; i += CHUNK) {
                const { data, error } = await supabase
                    .from('deals').select(DEAL_SELECT)
                    .in('id', ids.slice(i, i + CHUNK))
                    .neq('status', 'deleted');   // الحذف عندنا «ناعم» — لولا هذا لظهر المحذوف في المفضلة
                if (error) throw error;
                out.push(...(data || []).map(dealRepository.mapRowToDeal));
            }
            return out;
        } catch (e) {
            console.error('❌ Deals fetch by ids failed:', e);
            return out;   // ما وصل يُعرض — لا شاشة فارغة بسبب قطعةٍ واحدة
        }
    },

    save: async (deal: Deal): Promise<void> => {
        // Optimistic local state update should be handled by the caller (AppContext)
        // Persistence is now purely remote.

        // 2. Sync to Remote
        const dbDeal: Record<string, any> = {
            id: deal.id,
            store_id: deal.storeId,
            shop_name: deal.shopName,
            item_name: deal.itemName,
            category: deal.category,
            gender: deal.gender || 'all',
            size: deal.size || null,
            original_price: deal.originalPrice,
            discounted_price: deal.discountedPrice,
            discount_percentage: deal.discountPercentage,
            images: deal.images || [],
            description: deal.description || '',
            location_id: deal.locationId || null,
            google_maps_link: deal.googleMapsLink || null,
            map_lat: deal.mapLocation?.lat ?? null,
            map_lng: deal.mapLocation?.lng ?? null,
            // Denormalized region/city so the filter chain works for
            // deals whose location_id is a `custom_<ts>` (not in LOCATIONS).
            region: deal.region || null,
            city: deal.city || null,
            reliability_score: deal.reliabilityScore ?? 100,
            expires_in_minutes: deal.expiresInMinutes ?? 525600,
            expiry_type: deal.expiryType || null,
            expiry_date: deal.expiryDate || null,
            quantity: deal.quantity === 'unlimited' ? null : deal.quantity,
            is_unlimited: deal.quantity === 'unlimited',
            initial_quantity: deal.initialQuantity === 'unlimited' ? null : (deal.initialQuantity ?? (deal.quantity === 'unlimited' ? null : deal.quantity)),
            status: deal.status,
            created_at: deal.createdAt || Date.now(),
            // v11.20 — scheduled launch (Coming Soon). null = launches immediately.
            starts_at: typeof deal.startsAt === 'number' ? deal.startsAt : null,
            // v12.28 — merchant booking limits (0/undefined = unlimited → NULL)
            max_per_booking: deal.maxPerBooking || null,
            max_bookings_per_buyer: deal.maxBookingsPerBuyer || null,
            rebook_cooldown_minutes: deal.rebookCooldownMinutes || null,
            // v12.48 — وسم الموسم (null = عرض عادي). نافذة الوسم يحرسها tr_deal_season.
            season_id: deal.seasonId || null,
            // v12.53 — اختيارات المنتج (أقسام/خيارات/كميات) — null = بلا اختيارات
            options: (deal.options && deal.options.length) ? deal.options : null,
            // v12.61 — نسخ المنتج بأسعار مختلفة (تجريبي) — null = بلا نسخ
            variants: (deal.variants && deal.variants.length) ? deal.variants : null,
            // v12.88 — رمز الكاشير (SKU) للمنتج الأساسي (رموز الأنواع/الإضافات داخل JSONB)
            pos_sku: (deal.posSku && deal.posSku.trim()) ? deal.posSku.trim() : null,
            // v12.91 — العرض الواحد في عدة مواقع (null = موقع واحد كالمعتاد)
            locations: (deal.locations && deal.locations.length > 1) ? deal.locations : null,
            loc_qty_mode: (deal.locations && deal.locations.length > 1) ? (deal.locQtyMode || 'per_location') : null
        };

        // Internal 25s ceiling per attempt. The deal triggers
        // (handle_deal_smart_notifications + tr_enforce_location_cap +
        // tr_guard_deal_publish) plus AP-NE1 RTT can take a few seconds
        // on healthy 4G, but anything past 25s means the request is hung
        // (commonly: Supabase JS auth-refresh inTabLock on backgrounded
        // iOS Safari). Surfacing fast lets the caller try again instead
        // of holding the spinner against an outer 40s wall.
        let { error } = await withTimeout(
            supabase.from('deals').upsert(dbDeal) as unknown as Promise<{ error: any }>,
            25000
        );
        // Tolerate the case where the deals table is on an older schema that
        // hasn't yet picked up the expiry_type / expiry_date columns
        // (migration v8.16). Drop the new fields and retry once so the user
        // can still publish — the values will start sticking the moment the
        // migration runs, without forcing a hard release coupling.
        if (error && /expiry_(type|date)/i.test(error.message || '')) {
            const { expiry_type, expiry_date, ...legacyDeal } = dbDeal;
            const retry = await withTimeout(
                supabase.from('deals').upsert(legacyDeal) as unknown as Promise<{ error: any }>,
                25000
            );
            error = retry.error;
        }
        // Same tolerance if the region/city columns don't exist yet.
        if (error && /column "(region|city)"/i.test(error.message || '')) {
            const { region, city, ...noGeo } = dbDeal;
            const retry = await withTimeout(
                supabase.from('deals').upsert(noGeo) as unknown as Promise<{ error: any }>,
                25000
            );
            error = retry.error;
        }
        // v11.20 — same forward-compat trick for starts_at (Coming Soon).
        // If the migration hasn't yet landed in the target DB, drop the field
        // and retry so the merchant can still publish (without scheduling).
        if (error && /starts_at/i.test(error.message || '')) {
            const { starts_at, ...noStart } = dbDeal;
            const retry = await withTimeout(
                supabase.from('deals').upsert(noStart) as unknown as Promise<{ error: any }>,
                25000
            );
            error = retry.error;
        }
        if (error) {
            console.error('❌ Remote deal upsert failed:', error.message);
            throw error;
        }
        logger.log('✅ Deal saved to remote:', deal.id);
    },

    /**
     * Partial update for stock-only changes (e.g. a buyer booking decrements
     * quantity by N). Crucially this does NOT touch the `status` column, which
     * means the `tr_guard_deal_publish` trigger (BEFORE UPDATE OF status) does
     * NOT fire. Fixes the bug where buyers got SUBSCRIPTION_REQUIRED if the
     * merchant's subscription happened to be expired at booking time —
     * already-published deals must remain bookable.
     */
    /**
     * v15.02 — إعلانُ «المخزون الكامل» لعرضٍ واحد. `newQuantity` هو ما عند
     * التاجر فعلاً، **لا** المتاح: القاعدة تطرح المحجوز وتشتقّ المتاح.
     *
     * 🪤 وكانت هذه الدالّة تكتب `quantity` مباشرةً على الجدول. وبعد v15.03
     *    صار ذلك الكتابةَ المباشرة تُفسَّر إعلاناً — فتُرسل رقماً مطلقاً من
     *    قراءةٍ قديمة، والنتيجة مخزونٌ خاطئ بلا أيّ خطأ ظاهر. والمسار الصحيح
     *    نداءُ `merchant_set_stock` الذي يفحص الملكية ويقفل الصفّ ويشتقّ.
     *    (و«بلا حدّ» تبقى كتابةً مباشرة: لا مخزونَ يُشتقّ لعرضٍ بلا سقف.)
     */
    updateQuantity: async (dealId: string, newQuantity: number | 'unlimited'): Promise<void> => {
        if (newQuantity === 'unlimited') {
            const { error } = await supabase.from('deals')
                .update({ quantity: null, is_unlimited: true }).eq('id', dealId);
            if (error) { console.error('❌ unlimited toggle failed:', error.message); throw error; }
            logger.log('✅ Deal set unlimited:', dealId);
            return;
        }
        const { data, error } = await supabase.rpc('merchant_set_stock', {
            p_deal_id: dealId, p_on_hand: newQuantity, p_variants: null, p_locations: null,
        });
        if (error) { console.error('❌ merchant_set_stock failed:', error.message); throw error; }
        // 🪤 كتابةٌ ترفضها القاعدة تعود `error=null` — فلا يُقال «حُفظ» إلا بدليل.
        if (!(data as any)?.ok) {
            const code = (data as any)?.error || 'STOCK_WRITE_REFUSED';
            console.error('❌ merchant_set_stock refused:', code);
            throw new Error(code);
        }
        logger.log('✅ Stock declared:', dealId, '→', newQuantity, '· متاح:', (data as any).available);
    },

    /**
     * Save an entire deals array (bulk update).
     */
    saveDeals: async (deals: Deal[]): Promise<void> => {
        try {
            const dbDeals = deals.map(deal => ({
                id: deal.id,
                store_id: deal.storeId,
                shop_name: deal.shopName,
                item_name: deal.itemName,
                category: deal.category,
                gender: deal.gender,
                size: deal.size,
                original_price: deal.originalPrice,
                discounted_price: deal.discountedPrice,
                discount_percentage: deal.discountPercentage,
                images: deal.images,
                description: deal.description,
                location_id: deal.locationId || null,
                google_maps_link: deal.googleMapsLink || null,
                map_lat: deal.mapLocation?.lat || null,
                map_lng: deal.mapLocation?.lng || null,
                region: deal.region || null,
                city: deal.city || null,
                reliability_score: deal.reliabilityScore || 100,
                expires_in_minutes: deal.expiresInMinutes || 525600,
                quantity: deal.quantity === 'unlimited' ? null : deal.quantity,
                is_unlimited: deal.quantity === 'unlimited',
                initial_quantity: deal.initialQuantity === 'unlimited' ? null : (deal.initialQuantity ?? (deal.quantity === 'unlimited' ? null : deal.quantity)),
                status: deal.status,
                created_at: deal.createdAt || Date.now(),
                starts_at: typeof deal.startsAt === 'number' ? deal.startsAt : null,
                max_per_booking: deal.maxPerBooking || null,
                max_bookings_per_buyer: deal.maxBookingsPerBuyer || null,
                rebook_cooldown_minutes: deal.rebookCooldownMinutes || null
            }));
            const { error } = await supabase.from('deals').upsert(dbDeals);
            if (error) throw error;
            logger.log('✅ Deals bulk-saved to remote');
        } catch (e) {
            console.error('Remote bulk sync failed:', e);
        }
    },

    remove: async (id: string): Promise<void> => {
        // Soft-delete first: set status to 'deleted' so FK constraints (bookings)
        // don't block the operation. The client filters out 'deleted' deals on fetch.
        // If soft-delete succeeds, also attempt hard delete for cleanup.
        try {
            const { error: softError } = await supabase
                .from('deals')
                .update({ status: 'deleted' })
                .eq('id', id);
            if (softError) throw softError;
            logger.log('✅ Deal soft-deleted from remote');

            // Best-effort hard delete — may fail if bookings still reference it
            const { error: hardError } = await supabase.from('deals').delete().eq('id', id);
            if (hardError) {
                // Expected when bookings exist — soft delete is sufficient
                logger.log('ℹ️ Hard delete skipped (FK constraint), soft delete active');
            } else {
                logger.log('✅ Deal hard-deleted from remote');
            }
        } catch (e) {
            console.error('Remote delete failed:', e);
            throw e; // Let caller handle the error
        }
    },

    clearAll: async (): Promise<void> => {
        // Remote clear not allowed for safety
    },

    mapRowToDeal: (d: any): Deal => {
        const deal: Deal = {
            id: d.id,
            storeId: d.store_id,
            shopName: d.shop_name,
            itemName: d.item_name,
            category: d.category,
            gender: d.gender || 'all',
            size: d.size || undefined,
            originalPrice: Number(d.original_price) || 0,
            discountedPrice: Number(d.discounted_price) || 0,
            discountPercentage: Number(d.discount_percentage) || 0,
            images: Array.isArray(d.images) ? d.images : [],
            description: d.description || '',
            locationId: d.location_id || '',
            reliabilityScore: Number(d.reliability_score) || 100,
            expiresInMinutes: Number(d.expires_in_minutes) || 525600,
            quantity: d.is_unlimited ? 'unlimited' : (d.quantity ?? 0),
            // v15.02 — المخزون الكامل بجانب المتاح. يُقرأ ولا يُكتب: القاعدة
            // تشتقّه ممّا يكتبه التاجر في `quantity` (المشغّل tr_b0_stock_declare).
            onHand: d.is_unlimited ? null : (d.on_hand ?? null),
            soldOut: d.sold_out === true,   // v15.07 — مفتاح «نفد» اليدويّ
            initialQuantity: d.is_unlimited ? 'unlimited' : (d.initial_quantity ?? d.quantity ?? 0),
            ratings: [],
            authReal: 0,
            authFake: 0,
            myAuthVote: null,
            prepTime: d.prep_time || undefined,
            status: (d.status === 'expired' ? 'expired' : (d.status === 'paused' ? 'paused' : (d.status === 'deleted' ? 'deleted' as any : 'active'))),
            createdAt: isNaN(Number(d.created_at)) ? new Date(d.created_at).getTime() : Number(d.created_at)
        };

        // Only add location fields if they exist in the payload to avoid overwriting with undefined during partial realtime updates
        if ('google_maps_link' in d) deal.googleMapsLink = d.google_maps_link || undefined;
        if ('map_lat' in d && 'map_lng' in d) {
            deal.mapLocation = { lat: Number(d.map_lat), lng: Number(d.map_lng) };
        }
        if ('region' in d) deal.region = d.region || undefined;
        if ('city' in d) deal.city = d.city || undefined;
        if ('expiry_type' in d && d.expiry_type) deal.expiryType = d.expiry_type;
        if ('expiry_date' in d && d.expiry_date) deal.expiryDate = d.expiry_date;
        // v12.28 — merchant booking limits (NULL → undefined = unlimited)
        if ('max_per_booking' in d) deal.maxPerBooking = d.max_per_booking || undefined;
        if ('max_bookings_per_buyer' in d) deal.maxBookingsPerBuyer = d.max_bookings_per_buyer || undefined;
        if ('rebook_cooldown_minutes' in d) deal.rebookCooldownMinutes = d.rebook_cooldown_minutes || undefined;
        // v12.48 — وسم حملة الموسم
        if ('season_id' in d) deal.seasonId = d.season_id || undefined;
        // v12.53 — اختيارات المنتج
        if ('options' in d) deal.options = Array.isArray(d.options) ? d.options : undefined;
        // v12.61 — نسخ المنتج (تجريبي)
        if ('variants' in d) deal.variants = Array.isArray(d.variants) ? d.variants : undefined;
        // v12.88 — رمز الكاشير للمنتج الأساسي
        if ('pos_sku' in d) deal.posSku = d.pos_sku || undefined;
        // v12.91 — مواقع العرض المتعددة
        if ('locations' in d) deal.locations = Array.isArray(d.locations) ? d.locations : undefined;
        if ('loc_qty_mode' in d) deal.locQtyMode = d.loc_qty_mode || undefined;
        // v11.20 — scheduled launch (Coming Soon). Stored as BIGINT epoch ms.
        if ('starts_at' in d && d.starts_at != null) {
            deal.startsAt = isNaN(Number(d.starts_at)) ? new Date(d.starts_at).getTime() : Number(d.starts_at);
        }

        // Analytics counters (migration v13). Optional — older rows may be null.
        if ('views' in d && d.views != null)   deal.views  = Number(d.views)  || 0;
        if ('clicks' in d && d.clicks != null) deal.clicks = Number(d.clicks) || 0;

        // v13.24 — عدّادات مثبّتة على الصف بـtriggers في القاعدة. وجودها يُغني
        // الصفحة عن جولتَي شبكة (صفوف التقييمات + نداء عدّ الأصالة).
        if ('auth_real_count' in d && d.auth_real_count != null) deal.authReal = Number(d.auth_real_count) || 0;
        if ('auth_fake_count' in d && d.auth_fake_count != null) deal.authFake = Number(d.auth_fake_count) || 0;
        if ('rating_avg'   in d && d.rating_avg   != null) deal.ratingAvg   = Number(d.rating_avg)   || 0;
        if ('rating_count' in d && d.rating_count != null) deal.ratingCount = Number(d.rating_count) || 0;

        return deal;
    }
};
